import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Wallet, TrendingUp, Minus, Plus, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BudgetSimulator() {
  const { token, refreshUser } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [allocations, setAllocations] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    try {
      const response = await axios.get(`${API}/simulator/budget/scenarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScenarios(response.data);
    } catch (error) {
      toast.error('Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  };

  const startScenario = (scenario) => {
    setSelectedScenario(scenario);
    const initialAllocations = {
      savings: 0,
      entertainment: 0,
      extra: 0,
    };
    setAllocations(initialAllocations);
    setResult(null);
  };

  const calculateRemaining = () => {
    if (!selectedScenario) return 0;
    const fixedExpenses = selectedScenario.expenses
      .filter(e => e.fixed)
      .reduce((sum, e) => sum + e.amount, 0);
    const flexible = selectedScenario.expenses
      .filter(e => e.flexible)
      .reduce((sum, e) => sum + e.amount, 0);
    const allocated = Object.values(allocations).reduce((sum, val) => sum + val, 0);
    return selectedScenario.monthly_income - fixedExpenses - flexible - allocated;
  };

  const adjustAllocation = (key, delta) => {
    const remaining = calculateRemaining();
    const current = allocations[key] || 0;
    const newValue = Math.max(0, current + delta);
    
    if (delta > 0 && remaining < delta) {
      toast.error('Not enough remaining budget!');
      return;
    }
    
    setAllocations(prev => ({ ...prev, [key]: newValue }));
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post(`${API}/simulator/budget/submit`, {
        scenario_id: selectedScenario.id,
        decisions: allocations
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data);
      toast.success(`+${response.data.xp_earned} XP earned!`);
      await refreshUser();
    } catch (error) {
      toast.error('Failed to submit budget');
    }
  };

  const remaining = calculateRemaining();
  const fixedExpenses = selectedScenario?.expenses?.filter(e => e.fixed) || [];
  const flexibleExpenses = selectedScenario?.expenses?.filter(e => e.flexible) || [];

  // Scenario Selection
  if (!selectedScenario) {
    return (
      <Layout>
        <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="budget-simulator">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 
              className="text-3xl lg:text-4xl font-black mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <span className="text-[#0A1F44]">Budget</span> Simulator
            </h1>
            <p className="text-[#A3A3A3] mb-8">Practice managing your money with real SA scenarios!</p>

            <div className="grid gap-4">
              {loading ? (
                <div className="text-center py-12 text-[#C0975C] animate-pulse">Loading...</div>
              ) : (
                scenarios.map((scenario) => (
                  <button
                    key={scenario.id}
                    onClick={() => startScenario(scenario)}
                    className="neo-card p-6 text-left group card-hover"
                    data-testid={`scenario-${scenario.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-[#0A1F44] flex items-center justify-center flex-shrink-0">
                        <Wallet className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-lg">{scenario.title}</h3>
                          <span className={`badge ${scenario.difficulty === 'easy' ? 'badge-primary' : 'badge-secondary'}`}>
                            {scenario.difficulty}
                          </span>
                        </div>
                        <p className="text-[#A3A3A3] mb-3">{scenario.description}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-[#C0975C] font-mono">R{scenario.monthly_income.toLocaleString()}/month</span>
                          <span className="text-[#A3A3A3] text-sm">Goal: {scenario.goal}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  // Result View
  if (result) {
    return (
      <Layout>
        <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="budget-result">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="neo-card p-8 text-center"
          >
            <div className="w-20 h-20 bg-[#0A1F44] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            
            <h2 
              className="text-3xl font-black mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Budget Complete!
            </h2>
            <p className="text-[#A3A3A3] mb-6">{result.feedback}</p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-white/5">
                <div className="text-3xl font-black text-[#C0975C]">{result.score}%</div>
                <div className="text-sm text-[#A3A3A3]">Score</div>
              </div>
              <div className="p-4 bg-white/5">
                <div className="text-3xl font-black text-[#0A1F44]">+{result.xp_earned}</div>
                <div className="text-sm text-[#A3A3A3]">XP Earned</div>
              </div>
            </div>

            <div className="text-left mb-6">
              <h4 className="font-bold mb-2">Tips:</h4>
              <ul className="space-y-1">
                {result.tips.map((tip, i) => (
                  <li key={i} className="text-[#A3A3A3] text-sm flex items-start gap-2">
                    <span className="text-[#C0975C]">•</span> {tip}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setSelectedScenario(null);
                  setResult(null);
                }}
                className="neo-button-secondary flex-1 py-3"
              >
                Try Another
              </button>
              <button
                onClick={() => startScenario(selectedScenario)}
                className="neo-button-primary flex-1 py-3"
              >
                Retry
              </button>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  // Budget Planning View
  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="budget-planning">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button
            onClick={() => setSelectedScenario(null)}
            className="text-[#A3A3A3] hover:text-white text-sm mb-4"
          >
            ← Back to scenarios
          </button>

          <h1 
            className="text-2xl lg:text-3xl font-black mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {selectedScenario.title}
          </h1>
          <p className="text-[#A3A3A3] mb-6">{selectedScenario.description}</p>

          {/* Income */}
          <div className="neo-card p-5 mb-4">
            <div className="flex items-center justify-between">
              <span className="font-bold">Monthly Income</span>
              <span className="text-2xl font-black text-[#C0975C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                R{selectedScenario.monthly_income.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Fixed Expenses */}
          <div className="neo-card p-5 mb-4">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-[#FF3333]"></span>
              Fixed Expenses
            </h3>
            <div className="space-y-3">
              {fixedExpenses.map((expense) => (
                <div key={expense.name} className="flex items-center justify-between text-sm">
                  <span className="text-[#A3A3A3]">{expense.name}</span>
                  <span className="font-mono">-R{expense.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Flexible Expenses */}
          <div className="neo-card p-5 mb-4">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-[#C0975C]"></span>
              Flexible Expenses (Estimated)
            </h3>
            <div className="space-y-3">
              {flexibleExpenses.map((expense) => (
                <div key={expense.name} className="flex items-center justify-between text-sm">
                  <span className="text-[#A3A3A3]">{expense.name}</span>
                  <span className="font-mono">-R{expense.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Your Allocations */}
          <div className="neo-card p-5 mb-4">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-[#C0975C]"></span>
              Your Decisions
            </h3>
            
            {[
              { key: 'savings', label: 'Savings', color: '#C0975C' },
              { key: 'entertainment', label: 'Entertainment', color: '#0A1F44' },
              { key: 'extra', label: 'Extra/Emergency', color: '#C0975C' },
            ].map(({ key, label, color }) => (
              <div key={key} className="flex items-center justify-between py-3 border-b border-white/10 last:border-0">
                <span>{label}</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => adjustAllocation(key, -100)}
                    className="w-8 h-8 bg-white/10 flex items-center justify-center hover:bg-white/20"
                    data-testid={`decrease-${key}`}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-mono text-lg w-24 text-center" style={{ color }}>
                    R{(allocations[key] || 0).toLocaleString()}
                  </span>
                  <button
                    onClick={() => adjustAllocation(key, 100)}
                    className="w-8 h-8 bg-white/10 flex items-center justify-center hover:bg-white/20"
                    data-testid={`increase-${key}`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Remaining */}
          <div className={`neo-card p-5 mb-6 ${remaining < 0 ? 'border-[#FF3333]' : 'border-[#C0975C]'}`}>
            <div className="flex items-center justify-between">
              <span className="font-bold">Remaining</span>
              <span 
                className={`text-2xl font-black ${remaining < 0 ? 'text-[#FF3333]' : 'text-[#C0975C]'}`}
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                R{remaining.toLocaleString()}
              </span>
            </div>
            {remaining < 0 && (
              <p className="text-[#FF3333] text-sm mt-2">You're over budget! Adjust your allocations.</p>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={remaining < 0}
            className="neo-button-primary w-full py-4 disabled:opacity-50"
            data-testid="submit-budget-btn"
          >
            Submit Budget
          </button>
        </motion.div>
      </div>
    </Layout>
  );
}
